import MultiSelectCheck from '../common/MultiSelectCheck';
import { LOCALES } from '@/constants/locales';
import { TIME_ZONES } from '@/constants/timeZones';
import InfoIcon from '@mui/icons-material/Info';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Checkbox, FormControlLabel, Switch, TextField, Typography, useTheme } from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch, type SubmitHandler } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { Perm } from '../../permissions/permissions';
import { usePermissions } from '../../permissions/usePermissions';
import { fillHeight } from "../../theme";
import SaveFooter from '../common/SaveFooter';
import Spacer from '../common/Spacer';
import { useAlert } from '../common/alert/AlertProvider';
import { useToast } from '../common/toast/ToastProvider';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ProviderConfig from './ProviderConfig';
import FormTextField from '../common/FormTextField';
import { isValidEmail } from '../../validation/fieldValidators';
import LinearProgress from '@mui/material/LinearProgress';
import TabWithError from '../common/TabWithError';
import { Brand, createBrand } from '@/models/account-management/Brand';
import { brandApi } from '@/api/account-management/brandApi';
import { BrandType } from '@/models/enums/BrandType';
import { prettifyEnumName } from '@/models/enums/EnumHelper';
import BitFlagSelect from '../common/BitFlagSelect';
import { LoyaltySignInType } from '@/models/enums/LoyaltySignInType';
import { ProviderKind } from '@/models/enums/ProviderKind';
import { providerConfigurationApi } from '@/api/configuration/providerConfigurationApi';
import { type Provider } from '@/models/configuration/ProviderConfiguration';
import { useSettleDiscard } from '@/hooks/useSettleDiscard';
import { useUnsavedChangesPrompt } from '@/hooks/useUnsavedChangesPrompt';
import { knoxConfigApi } from '@/api/knox/knoxConfigApi';
import { receiptSettingsApi } from '@/api/receipt/receiptSettingsApi';
import { createReceiptSettings, type ReceiptSettings } from '@/models/receipt/ReceiptSettings';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { type KnoxConfig } from '@/models/knox/KnoxConfig';

const BrandDetails = () => {
    const { id } = useParams<{ id: string }>();
    const isEditing = id !== 'new';
    const [searchParams] = useSearchParams();
    const organizationId = searchParams.get("orgId");
    const [tabValue, setTabValue] = useState<string>('detail');
    // Where to go once the form is clean again — see handleSaveSuccess. Held rather than navigated to
    // immediately, because the unsaved-changes blocker reads formState.isDirty and that does not update
    // until React re-renders.
    const [navigateAfterSave, setNavigateAfterSave] = useState<string | null>(null);
    const methods = useForm<Brand>(!isEditing ? { defaultValues: createBrand(organizationId!) } : undefined);
    const { control, reset, register, handleSubmit: onFormSubmit, formState, watch, setValue, getValues } = methods;
    // Prompt before leaving with unsaved edits on any tab. Called further down, once navigate/app exist —
    // the prompt needs to know how to leave this screen, not just that it should ask.
    const allowSignUp = useWatch({ control, name: `loyaltyConfiguration.allowSignUp` });
    const loyaltyVendor = useWatch({ control, name: `loyaltyConfiguration.vendor` }); // "" / undefined = None (no loyalty config)
    const { data: brand, isPending } = useQuery<Brand>({ queryKey: ['brand', id], enabled: isEditing, queryFn: () => brandApi.get(id!), staleTime: 0, refetchOnMount: 'always', refetchOnWindowFocus: false, refetchOnReconnect: false });
    const { data: loyaltyProviders } = useQuery<Provider[]>({ queryKey: ['providers', ProviderKind.Loyalty], queryFn: () => providerConfigurationApi.listProviders(ProviderKind.Loyalty), staleTime: Infinity });
    // Device tab (Knox). The vendor + creds live in KnoxAPI (keyed by brand), NOT on the Brand model — so this
    // is separate local state persisted alongside the brand save. "" vendor = None.
    const emptyKnox: KnoxConfig = { region: 'Global', clientId: '', clientSecret: '', efotaLicenseKey: null, managedTenantId: null, kaiEnabled: false };
    const [deviceVendor, setDeviceVendor] = useState<string>('');
    const [knoxConfig, setKnoxConfig] = useState<KnoxConfig>(emptyKnox);
    const [deviceDirty, setDeviceDirty] = useState(false);
    const [deviceError, setDeviceError] = useState(false);
    const [deviceConfigLoaded, setDeviceConfigLoaded] = useState(false); // a Knox config existed on load (drives delete-on-None)
    const { data: loadedKnox } = useQuery<KnoxConfig | null>({ queryKey: ['knox-config', id], enabled: isEditing, queryFn: () => knoxConfigApi.get(id!), staleTime: 0, refetchOnWindowFocus: false });
    // Receipt tab. Like the Device tab above, these live in their own service (TouchTab.Receipt, keyed by
    // brand) rather than on the Brand model, so they are local state saved alongside the brand.
    const [receiptSettings, setReceiptSettings] = useState<ReceiptSettings>(createReceiptSettings());
    const [receiptDirty, setReceiptDirty] = useState(false);
    const { data: loadedReceiptSettings } = useQuery<ReceiptSettings>({ queryKey: ['receipt-settings', id], enabled: isEditing, queryFn: () => receiptSettingsApi.get(id!), staleTime: 0, refetchOnWindowFocus: false });
    const { mutateAsync: saveBrand, isPending: isSaving } = useMutation({ mutationFn: brandApi.save, onError: () => handleSaveError() });
    const qc = useQueryClient();
    const toast = useToast();
    const alert = useAlert();
    const theme = useTheme();
    const strings = useLocalizedStrings();
    const canUpdate = usePermissions().canUpdate(Perm.adminConfiguration);
    const settleDiscard = useSettleDiscard(methods);
    const app = useAppContext();
    const navigate = useNavigate();

    // Prompt before leaving with unsaved edits on any tab.
    //
    // The second argument is how this screen leaves itself once the user has answered Leave somewhere the
    // router cannot see — switching org/brand/location. Without it the scope changed and this editor stayed
    // open, still dirty, still showing a brand from the scope just left (TTD-149).
    //
    // Back to the list rather than history: after a scope change the previous entry may well be this same
    // editor, and no orgId is carried because the one in the URL belongs to the org being left.
    const leaveToBrandList = useCallback(
        () => navigate(`/${app.organizationSlug}/admin/configuration/organizations`),
        [navigate, app.organizationSlug]
    );
    useUnsavedChangesPrompt(formState.isDirty, leaveToBrandList);

    // Load selected brand into the form when fetched
    useEffect(() => {
        app.setTitle(!isEditing ? strings.brandNewTitle : strings.brandEditTitle.replace("{{name}}", brand?.name ?? ""));
        reset(brand);
    }, [brand, reset]);

    // Put the app bar title back when this screen goes away. It was only ever set on the way in, so the
    // brand's name stayed in the bar over whatever the user navigated to next — closing by Save and by
    // Cancel both left it stale, which is why this hangs off unmount rather than the cancel handler.
    useEffect(() => {
        return () => { app.setTitle(strings.configuration); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!brand?.id) return;

    }, [brand?.id]);

    // Populate the Device tab from the loaded Knox config (present => vendor "Knox").
    useEffect(() => {
        if (loadedKnox === undefined) return;
        if (loadedKnox) {
            setDeviceVendor('Knox');
            setKnoxConfig(loadedKnox);
            setDeviceConfigLoaded(true);
        } else {
            setDeviceVendor('');
            setKnoxConfig(emptyKnox);
            setDeviceConfigLoaded(false);
        }
        setDeviceDirty(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadedKnox]);

    // A brand with nothing stored reads back as the defaults, so there is no "absent" branch here.
    useEffect(() => {
        if (!loadedReceiptSettings) return;
        setReceiptSettings(loadedReceiptSettings);
        setReceiptDirty(false);
    }, [loadedReceiptSettings]);

    const updateKnox = (patch: Partial<KnoxConfig>) => { setKnoxConfig((c) => ({ ...c, ...patch })); setDeviceDirty(true); };

    useEffect(() => {
            if (allowSignUp === false && getValues(`loyaltyConfiguration.signUpUrl`)) {
                setValue(`loyaltyConfiguration.signUpUrl`, null, { shouldDirty: true, shouldValidate: false });
            }
        }, [allowSignUp]);

    const handleSubmit: SubmitHandler<Brand> = async (data) => {
        // Device tab (Knox) validation — its fields aren't in react-hook-form, so validate here.
        if (deviceVendor === 'Knox' && (!knoxConfig.region.trim() || !knoxConfig.clientId.trim() || !knoxConfig.clientSecret.trim())) {
            setDeviceError(true);
            setTabValue('deviceConfig');
            toast({ severity: 'error', message: strings.knoxConfigIncomplete });
            return;
        }
        setDeviceError(false);

        // checkoutPrompt and receiptRetroClaimPrompt are no longer edited here - they are content
        // strings now (Configuration > Content Strings, category Loyalty). They are still SENT, and
        // deliberately so: the spread below carries them straight back from what was loaded, because
        // react-hook-form keeps defaultValues for fields that were never registered. Dropping them
        // from the payload would null the columns that GuestPayAPI still falls back to.

        // "None" (no vendor selected) means the brand has no loyalty configuration.
        // Coerce allowSignUp to a real boolean — the checkbox leaves it undefined until toggled,
        // which would otherwise send null for a required bool.
        const loyalty = data.loyaltyConfiguration;
        const cleaned: Brand = {
            ...data,
            loyaltyConfiguration: loyalty && loyalty.vendor
                ? { ...loyalty, allowSignUp: !!loyalty.allowSignUp }
                : null
        };

        let saved: Brand | void;
        try {
            saved = await saveBrand(cleaned);
        } catch {
            return; // onError toast already fired
        }

        // Persist the Knox config (separate service, keyed by brand). Needs the brand id — for a new brand it
        // comes back from the create.
        const brandId = (saved as Brand | undefined)?.id ?? (isEditing ? id : undefined);
        if (brandId && brandId !== 'new') {
            try {
                if (deviceVendor === 'Knox') await knoxConfigApi.save(brandId, knoxConfig);
                else if (deviceConfigLoaded) await knoxConfigApi.remove(brandId);
            } catch {
                toast({ severity: 'error', message: strings.knoxConfigSaveError });
                return;
            }

            // Only when touched. Every brand save would otherwise write a settings row for a brand nobody
            // has configured a receipt for, which is a write the service does not need to be given.
            if (receiptDirty) {
                try {
                    await receiptSettingsApi.save(brandId, receiptSettings);
                } catch {
                    toast({ severity: 'error', message: strings.receiptSettingsSaveError });
                    return;
                }
            }
        }

        await handleSaveSuccess();
    }

    const handleInvalid = (errors: any) => {
        console.log("submit invalid", errors);

        // Say something. A required field can sit on a tab the user is not looking at - time zone on
        // Details while they are on Feedback - and without this the Save button simply did nothing: the
        // field marked itself in red on a panel nobody could see, no toast fired, and there was no way to
        // find out why the form would not save.
        //
        // Jump to the first tab (in tab-bar order) carrying an error, the same way LocationDetails does,
        // and toast as well - switching tabs alone still leaves the user to spot the red field themselves.
        // Only the tabs that have validation rules to fail. The Device tab is not here because it is not
        // part of the form - handleSubmit checks it and switches tabs itself before submitting.
        const tabOrder: { tab: string; hasError: () => boolean }[] = [
            { tab: 'detail', hasError: () => ['name', 'type', 'timeZoneId'].some((k) => !!errors[k]) },
            { tab: 'loyaltyConfig', hasError: () => !!errors.loyaltyConfiguration },
        ];
        const firstErrored = tabOrder.find((t) => t.hasError());
        if (firstErrored) setTabValue(firstErrored.tab);

        toast({ severity: 'error', message: strings.validationRequiredFieldsMissing });
    };

    const handleSaveSuccess = async () => {
        toast({ severity: "success", message: strings.brandSaveSuccess });
        // Refresh the brand list(s) so the newly created/updated brand shows in the grid.
        // Use the ['brands'] prefix (matches any org) since organizationId isn't in the URL when editing.
        await qc.invalidateQueries({ queryKey: ['brands'] });
        if (!isEditing) {
            // Clear the dirty flag, then leave — but NOT in the same tick.
            //
            // useUnsavedChangesPrompt blocks on formState.isDirty, and React batches the state update that
            // reset() schedules. Calling navigate() straight afterwards ran while isDirty was still true, so
            // the blocker asked "discard your changes?" about the changes it had just saved. That is what
            // made this ticket look fixed when it was not: the reset was correct and simply had not taken
            // effect yet.
            //
            // Recording where to go instead lets the effect below navigate once the form is genuinely clean.
            reset(getValues());
            setNavigateAfterSave(`/${app.organizationSlug}/admin/configuration/organizations?orgId=${organizationId}`);
        } else {
            // Refresh this brand's detail cache (calls GetBrand again) so re-opening the editor shows the saved values
            await qc.invalidateQueries({ queryKey: ['brand', id] });
            await qc.invalidateQueries({ queryKey: ['knox-config', id] });
            await qc.invalidateQueries({ queryKey: ['receipt-settings', id] });
            const values = getValues();
            reset(values);
            setDeviceDirty(false);
            setReceiptDirty(false);
            setDeviceConfigLoaded(deviceVendor === 'Knox');
        }
    }

    // Leave only once the form is actually clean, so the unsaved-changes blocker has nothing to object to.
    // Guarded on isDirty rather than firing on the next render, because that is the condition the blocker
    // itself checks — if it is somehow still dirty, staying put and showing the prompt is the safe outcome.
    useEffect(() => {
        if (navigateAfterSave && !formState.isDirty) {
            const target = navigateAfterSave;
            setNavigateAfterSave(null);
            navigate(target);
        }
    }, [navigateAfterSave, formState.isDirty, navigate]);

    const handleSaveError = () => {
        toast({ severity: "error", message: strings.brandSaveError });
    }

    // This maps the component's errors on each tab
    const tabErrors = {
        detail: !!(
            formState.errors.name
            || formState.errors.type
            || formState.errors.timeZoneId
        ),
        loyalty: !!formState.errors.loyaltyConfiguration,
        device: deviceError
    } as const;

    return (
        <Box sx={{ ...fillHeight, overflow: 'hidden' }}>
            <FormProvider {...methods}>
                <form onSubmit={onFormSubmit(handleSubmit, handleInvalid)} id="location-form" style={{ height: '100%' }}>
                    <TabContext value={tabValue}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: (theme) => theme.palette.background.paper }}>
                            <TabList onChange={(_event, newValue) => setTabValue(newValue)} aria-label="tabs"
                                sx={{
                                    minHeight: 48,
                                    '& .MuiTab-root': {
                                        minHeight: 48,
                                    }
                                }}
                            >
                                <TabWithError icon={<InfoIcon />} iconPosition="start" label={strings.details} value="detail" hasError={tabErrors.detail} />
                                <TabWithError icon={<FavoriteIcon />} iconPosition="start" label={strings.loyalty} value="loyaltyConfig" hasError={tabErrors.loyalty} />
                                <TabWithError icon={<ThumbUpIcon />} iconPosition="start" label={strings.feedback} value="feedbackConfig" hasError={false} />
                                <TabWithError icon={<PhoneAndroidIcon />} iconPosition="start" label={strings.device} value="deviceConfig" hasError={tabErrors.device} />
                                <TabWithError icon={<ReceiptLongIcon />} iconPosition="start" label={strings.receipt} value="receiptConfig" hasError={false} />
                            </TabList>
                        </Box>
                        {isEditing && isPending ? (<LinearProgress />) :
                            <Box sx={{ height: '100%' }}>
                                <TabPanel id="detail" value="detail" keepMounted>
                                    <Stack direction="row" spacing={2} divider={<Divider orientation="vertical" flexItem />}>
                                        <Box>
                                            <FormTextField
                                                id="name"
                                                label={strings.name}
                                                variant="outlined"
                                                sx={{ width: 600 }}
                                                {...register("name", {
                                                    required: strings.validationNameRequired
                                                })}
                                                isEditing={isEditing}
                                                error={!!formState.errors.name}
                                                helperText={formState.errors.name?.message || " "}
                                            />
                                            <Spacer />
                                            <FormControl sx={{ width: 400 }} error={!!formState.errors.type}>
                                                <InputLabel id="brand-type-label">{strings.type}</InputLabel>
                                                <Controller
                                                    name="type"
                                                    control={control}
                                                    rules={{ required: strings.validationTypeRequired }}
                                                    render={({ field }) => (
                                                        <Select
                                                            labelId="brand-type-label"
                                                            id="brand-type"
                                                            label={strings.type}
                                                            disabled={isEditing}
                                                            {...field}
                                                            value={field.value ?? ""}
                                                        >
                                                            {Object.entries(BrandType).map(([name, value]) => (
                                                                <MenuItem key={value} value={value}>{prettifyEnumName(name)}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    )}
                                                />
                                                <FormHelperText>{formState.errors.type?.message || " "}</FormHelperText>
                                            </FormControl>
                                            <Spacer />
                                            <FormControl sx={{ width: 400 }} error={!!formState.errors.timeZoneId}>
                                                <InputLabel id="time-zone-label">{strings.timeZone}</InputLabel>
                                                <Controller
                                                    name="timeZoneId"
                                                    control={control}
                                                    rules={{ required: strings.validationTimeZoneRequired }}
                                                    render={({ field }) => (
                                                        <Select
                                                            labelId="time-zone-label"
                                                            id="time-zone-id"
                                                            label={strings.timeZone}
                                                            {...field}
                                                            value={field.value ?? ""}
                                                        >
                                                            {TIME_ZONES.map((s) => (
                                                                <MenuItem key={s.id} value={s.id}>
                                                                    {s.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    )}
                                                />
                                                <FormHelperText>{formState.errors.timeZoneId?.message || " "}</FormHelperText>
                                            </FormControl>
                                            <Spacer />
                                            {/* The languages this brand operates in. This set is what the
                                                location form offers as locale choices and what the content
                                                strings screen shows as columns - declaring it here is what
                                                lets a brand write French copy BEFORE opening in Quebec.

                                                Removing one a location still uses is refused server-side,
                                                and that refusal arrives as a 409 naming the locales. */}
                                            <FormControl sx={{ width: 400 }}>
                                                <InputLabel id="supported-locales-label">{strings.supportedLocales}</InputLabel>
                                                <Controller
                                                    name="supportedLocales"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <MultiSelectCheck<string>
                                                            labelId="supported-locales-label"
                                                            id="supported-locales"
                                                            label={strings.supportedLocales}
                                                            value={field.value ?? []}
                                                            onChange={field.onChange}
                                                            options={LOCALES.map(l => ({ value: l.code, label: l.name, secondary: l.code }))}
                                                        />
                                                    )}
                                                />
                                                <FormHelperText>{strings.supportedLocalesHelp}</FormHelperText>
                                            </FormControl>
                                            <Spacer />
                                            <FormTextField
                                                id="onboarding-email"
                                                label={strings.onboardingEmail}
                                                variant="outlined"
                                                sx={{ width: 400 }}
                                                // Optional (nullable): only validate the format once something is entered.
                                                {...register("onboardingEmail", {
                                                    validate: (v?: string | null) => !v?.trim() || isValidEmail(v) || strings.validationEmailInvalid,
                                                })}
                                                isEditing={isEditing}
                                                error={!!formState.errors.onboardingEmail}
                                                helperText={formState.errors.onboardingEmail?.message || " "}
                                            />
                                            <Spacer />
                                        </Box>
                                    </Stack>
                                </TabPanel>
                                <TabPanel id="loyaltyConfig" value="loyaltyConfig" keepMounted>
                                    <Stack direction="row" spacing={2} divider={<Divider orientation="vertical" flexItem />}>
                                        <Box>
                                            <FormControl sx={{ width: 400 }}>
                                                <InputLabel id="loyalty-vendor-label">{strings.vendor}</InputLabel>
                                                <Controller
                                                    name={`loyaltyConfiguration.vendor`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Select
                                                            id="loyalty-vendor"
                                                            labelId="loyalty-vendor-label"
                                                            label={strings.vendor}
                                                            value={field.value ?? ""}
                                                            onChange={(e) => field.onChange(e.target.value)}
                                                            onBlur={field.onBlur}
                                                            inputRef={field.ref}
                                                        >
                                                            <MenuItem value="">None</MenuItem>
                                                            {(loyaltyProviders ?? []).map((p) => (
                                                                <MenuItem key={p.vendor} value={p.vendor}>{p.vendor}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    )}
                                                />
                                            </FormControl>
                                            {loyaltyVendor && (
                                            <>
                                            <Spacer y={3} />
                                            <FormTextField
                                                id="loyaltyProgramName"
                                                label={strings.loyaltyProgramName}
                                                variant="outlined"
                                                sx={{ width: 400 }}
                                                {...register(`loyaltyConfiguration.programName`, {
                                                    validate: (v) => !getValues(`loyaltyConfiguration.vendor`) || (typeof v === 'string' && v.trim().length > 0) || strings.validationLoyaltyProgramNameRequired
                                                })}
                                                isEditing={isEditing}
                                                error={!!formState.errors.loyaltyConfiguration?.programName}
                                                helperText={formState.errors.loyaltyConfiguration?.programName?.message || " "}
                                            />
                                            <Spacer y={.5} />
                                            <FormControl sx={{ width: 200 }} error={!!formState.errors.loyaltyConfiguration?.loyaltySignInType}>
                                                <InputLabel id="loyalty-signin-type-label">{strings.loyaltySignInType}</InputLabel>
                                                <Controller
                                                    name={`loyaltyConfiguration.loyaltySignInType`}
                                                    control={control}
                                                    rules={{ validate: (v) => !getValues(`loyaltyConfiguration.vendor`) || (Number(v ?? 0) !== 0) || strings.validationLoyaltySignInTypeRequired }}
                                                    render={({ field }) => (
                                                        <BitFlagSelect
                                                            id="loyalty-signin-type"
                                                            labelId="loyalty-signin-type-label"
                                                            label={strings.loyaltySignInType}
                                                            value={field.value ?? 0}
                                                            onChange={field.onChange}
                                                            options={Object.entries(LoyaltySignInType).map(([name, value]) => ({ value, label: prettifyEnumName(name) }))}
                                                        />
                                                    )}
                                                />
                                                <FormHelperText>{formState.errors.loyaltyConfiguration?.loyaltySignInType?.message || " "}</FormHelperText>
                                            </FormControl>
                                            <Spacer y={.5} />
                                            <FormControl sx={{ width: 200 }}>
                                                <InputLabel id="loyalty-max-redemptions-label">{strings.loyaltyMaxRedemptionsPerOrder}</InputLabel>
                                                <Controller
                                                    name={`loyaltyConfiguration.maxRedemptionsPerOrder`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Select
                                                            id="loyalty-max-redemptions"
                                                            labelId="loyalty-max-redemptions-label"
                                                            label={strings.loyaltyMaxRedemptionsPerOrder}
                                                            value={field.value == null ? 'unlimited' : field.value}
                                                            onChange={(e) => field.onChange(e.target.value === 'unlimited' ? null : Number(e.target.value))}
                                                            onBlur={field.onBlur}
                                                            inputRef={field.ref}
                                                        >
                                                            <MenuItem value="unlimited">{strings.unlimited}</MenuItem>
                                                            {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                                                                <MenuItem key={n} value={n}>{n}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    )}
                                                />
                                            </FormControl>
                                            <Spacer y={3} />
                                            <FormTextField
                                                id="loyaltyPointsPerDollar"
                                                type="number"
                                                label={strings.loyaltyPointsPerDollar}
                                                variant="outlined"
                                                sx={{ width: 200 }}
                                                // Points are earned, never taken away, so the field starts at zero
                                                // and the spinner cannot step below it.
                                                slotProps={{ htmlInput: { min: 0, step: 1 } }}
                                                {...register(`loyaltyConfiguration.pointsPerDollar`, {
                                                    setValueAs: (v) => v === '' || v == null ? undefined : Number(v),
                                                    min: { value: 0, message: strings.validationPointsPerDollarNegative }
                                                })}
                                                error={!!formState.errors.loyaltyConfiguration?.pointsPerDollar}
                                                helperText={formState.errors.loyaltyConfiguration?.pointsPerDollar?.message || " "}
                                                isEditing={isEditing}
                                            />
                                            <Spacer y={3} />
                                            <Stack direction="row" spacing={2} alignItems="center">
                                                <FormControl size="input">
                                                    <FormControlLabel
                                                        label={strings.loyaltyAllowSignUp}
                                                        control={
                                                            <Controller
                                                                name={`loyaltyConfiguration.allowSignUp`}
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <Checkbox checked={field.value ?? false} onChange={(e) => field.onChange(e.target.checked)} />
                                                                )}
                                                            />
                                                        }
                                                    />
                                                </FormControl>
                                                <FormTextField
                                                    id="loyaltySignUpUrl"
                                                    label={strings.loyaltySignUpUrl}
                                                    variant="outlined"
                                                    sx={{ width: 440 }}
                                                    disabled={!allowSignUp}
                                                    {...register(`loyaltyConfiguration.signUpUrl`, {
                                                        validate: (v) => !getValues(`loyaltyConfiguration.vendor`) || !getValues(`loyaltyConfiguration.allowSignUp`) || (typeof v === 'string' && v.trim().length > 0) || strings.validationLoyaltySignUpUrlRequired
                                                    })}
                                                    isEditing={isEditing}
                                                    error={!!formState.errors.loyaltyConfiguration?.signUpUrl}
                                                    helperText={formState.errors.loyaltyConfiguration?.signUpUrl?.message || " "}
                                                />
                                            </Stack>
                                            </>
                                            )}
                                        </Box>
                                    </Stack>
                                </TabPanel>
                                <TabPanel id="deviceConfig" value="deviceConfig" keepMounted>
                                    <Stack direction="row" spacing={2} divider={<Divider orientation="vertical" flexItem />}>
                                        <Box>
                                            <FormControl sx={{ width: 400 }}>
                                                <InputLabel id="device-vendor-label">{strings.vendor}</InputLabel>
                                                <Select
                                                    id="device-vendor"
                                                    labelId="device-vendor-label"
                                                    label={strings.vendor}
                                                    value={deviceVendor}
                                                    onChange={(e) => { setDeviceVendor(e.target.value); setDeviceDirty(true); }}
                                                >
                                                    <MenuItem value="">None</MenuItem>
                                                    <MenuItem value="Knox">Knox</MenuItem>
                                                </Select>
                                            </FormControl>
                                            {deviceVendor === 'Knox' && (
                                                <>
                                                    <Spacer y={3} />
                                                    <TextField
                                                        label={strings.region}
                                                        variant="outlined"
                                                        sx={{ width: 400 }}
                                                        value={knoxConfig.region}
                                                        onChange={(e) => updateKnox({ region: e.target.value })}
                                                        error={deviceError && !knoxConfig.region.trim()}
                                                    />
                                                    <Spacer />
                                                    <TextField
                                                        label={strings.clientId}
                                                        variant="outlined"
                                                        sx={{ width: 400 }}
                                                        value={knoxConfig.clientId}
                                                        onChange={(e) => updateKnox({ clientId: e.target.value })}
                                                        error={deviceError && !knoxConfig.clientId.trim()}
                                                    />
                                                    <Spacer />
                                                    <TextField
                                                        label={strings.clientSecret}
                                                        type="password"
                                                        variant="outlined"
                                                        sx={{ width: 400 }}
                                                        value={knoxConfig.clientSecret}
                                                        onChange={(e) => updateKnox({ clientSecret: e.target.value })}
                                                        error={deviceError && !knoxConfig.clientSecret.trim()}
                                                    />
                                                    <Spacer />
                                                    <TextField
                                                        label={strings.knoxManagedTenantId}
                                                        variant="outlined"
                                                        sx={{ width: 400 }}
                                                        value={knoxConfig.managedTenantId ?? ''}
                                                        onChange={(e) => updateKnox({ managedTenantId: e.target.value || null })}
                                                        helperText={strings.knoxManagedTenantIdHelp}
                                                    />
                                                    <Spacer />
                                                    <FormControlLabel
                                                        label={strings.knoxAssetIntelligenceEnabled}
                                                        control={<Checkbox checked={knoxConfig.kaiEnabled} onChange={(e) => updateKnox({ kaiEnabled: e.target.checked })} />}
                                                    />
                                                </>
                                            )}
                                        </Box>
                                    </Stack>
                                </TabPanel>
                                <TabPanel id="feedbackConfig" value="feedbackConfig" keepMounted>
                                    <FormControl size="input">
                                        <FormControlLabel
                                            label={strings.touchTabFeedbackEnabled}
                                            control={
                                                <Controller
                                                    name={`touchTabFeedbackEnabled`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Switch checked={field.value ?? false} onChange={(e) => field.onChange(e.target.checked)} />
                                                    )}
                                                />
                                            }
                                        />
                                    </FormControl>
                                </TabPanel>
                                <TabPanel id="receiptConfig" value="receiptConfig" keepMounted>
                                    <FormControl size="input">
                                        <FormControlLabel
                                            label={strings.showPaymentTally}
                                            control={
                                                <Switch
                                                    checked={receiptSettings.showPaymentTally}
                                                    onChange={(e) => {
                                                        setReceiptSettings({ ...receiptSettings, showPaymentTally: e.target.checked });
                                                        setReceiptDirty(true);
                                                    }}
                                                />
                                            }
                                        />
                                        <FormHelperText>{strings.showPaymentTallyHelp}</FormHelperText>
                                    </FormControl>
                                </TabPanel>
                            </Box>
                        }
                    </TabContext>
                </form>
            </FormProvider>
            <SaveFooter
                readOnly={!canUpdate}
                formId="location-form"
                formIsDirty={formState.isDirty || deviceDirty || receiptDirty}
                saving={isSaving}
                saveButtonTitle={strings.save}
                cancelButtonTitle={strings.cancel}
                onCancel={() => navigate(-1)}
                // Cancel puts the form back and stays; Close (shown once clean) is what leaves. The Device
                // tab is separate local state and counts toward dirty above, so it has to be restored here
                // too or Cancel would look like it worked while leaving the brand dirty (TTD-161).
                onDiscard={() => {
                    reset(brand);
                    setDeviceVendor(loadedKnox ? 'Knox' : '');
                    setKnoxConfig(loadedKnox ?? emptyKnox);
                    setDeviceDirty(false);
                    setDeviceError(false);
                    setReceiptSettings(loadedReceiptSettings ?? createReceiptSettings());
                    setReceiptDirty(false);
                    // The reset alone leaves the form still claiming changes that no field accounts for,
                    // which trapped the footer on Cancel with no way off the screen (TTD-161, reopened).
                    settleDiscard();
                }}
            />
        </Box>
    );
};

export default BrandDetails;
